import { useId, useMemo, useState } from "react"
import { closestCenter, DndContext, KeyboardSensor, MouseSensor, TouchSensor, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core"
import { restrictToVerticalAxis } from "@dnd-kit/modifiers"
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { GripVerticalIcon, SettingsIcon } from "lucide-react"
import { EditableLabel } from "@/components/editable-text/editable-label"
import { EditableText } from "@/components/editable-text/editable-text"
import { handlebarsAutocomplete } from "@/components/editable-text/handlebars"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import type { CardProps, FlowGroups } from "./items"
import { localize, localized, useLanguage } from "./language"
import { ItemShell } from "./itemshell"
import { OptionSettingsDialog } from "./optionsettingsdialog"
import { OutcomeMenu } from "./outcomemenu"
import { RemoveButton } from "./removebutton"

function isEmptyOption(option: PlaybookOption) {
  return !option.label && !option.match?.length && !option.conditions?.length && !Object.keys(option.set ?? {}).length && !Object.keys(option.inc ?? {}).length && !option.say && !option.action
}

export function ItemCard({ item, vars, groups, index, issues, onUpdate, onRemove }: CardProps) {
  const templateExtensions = useMemo(() => [handlebarsAutocomplete(vars)], [vars])
  const language = useLanguage()
  const noMatch = item.on_no_match ?? {}
  const dndId = useId()
  const sensors = useSensors(useSensor(MouseSensor, {}), useSensor(TouchSensor, {}), useSensor(KeyboardSensor, {}))

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    onUpdate({ options: arrayMove(item.options!, Number(active.id), Number(over.id)) })
  }

  return <ItemShell item={item} vars={vars} index={index} issues={issues} onUpdate={onUpdate} onRemove={onRemove}>
    <EditableText value={localized(item, "say", language) ?? ""} onChange={say => onUpdate(localize(item, "say", say, language))} extensions={templateExtensions}
      placeholder={(language && item.say) || "No message"} />

    {item.options && <DndContext id={dndId} collisionDetection={closestCenter} modifiers={[restrictToVerticalAxis]} onDragEnd={handleDragEnd} sensors={sensors}>
      <SortableContext items={item.options.map((_, i) => String(i))} strategy={verticalListSortingStrategy}>
        <div className="flex flex-col gap-2">
          {item.options.map((option, i) => <OptionRow
            key={option.id ?? option.label ?? i}
            id={String(i)}
            option={option}
            vars={vars}
            groups={groups}
            onChange={next => onUpdate({ options: item.options!.map((o, j) => j === i ? next : o) })}
            onRemove={() => onUpdate({ options: item.options!.filter((_, j) => j !== i) })}
          />)}
        </div>
      </SortableContext>
    </DndContext>}

    {item.options && <EditableText
      value={localized(noMatch, "say", language) ?? ""}
      onChange={say => onUpdate({ on_no_match: localize(noMatch, "say", say, language) })}
      extensions={templateExtensions}
      placeholder={(language && noMatch.say) || "No fallback message"}
      className="text-xs"
    />}

    {!item.options && <div className="flex items-center gap-1 text-xs text-muted-foreground">
      <span>Captures into:</span>
      <EditableLabel value={item.set ?? ""} onChange={set => onUpdate({ set: set || undefined })} placeholder="No variable" className="text-xs" />
    </div>}
  </ItemShell>
}

interface OptionRowProps {
  id: string
  option: PlaybookOption
  vars: string[]
  groups: FlowGroups
  onChange: (option: PlaybookOption) => void
  onRemove: () => void
}

function OptionRow({ id, option, vars, groups, onChange, onRemove }: OptionRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  const language = useLanguage()

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return <div ref={setNodeRef} style={style} className="rounded-md border border-foreground/25 bg-muted/40 px-2 py-1 flex items-center gap-2">
    <span {...attributes} {...listeners} className="cursor-grab touch-none text-muted-foreground"><GripVerticalIcon className="size-4" /></span>
    <EditableLabel value={localized(option, "label", language) ?? ""} onChange={label => onChange(localize(option, "label", label, language))}
      placeholder={(language && option.label) || "No label"} className="flex-1 text-sm" />
    <OutcomeMenu outcome={option.action} groups={groups} onChange={action => onChange({ ...option, action: action.type === "next" ? undefined : action })} />
    <OptionMenu option={option} vars={vars} onChange={onChange} />
    <RemoveButton what="option" name={option.label ?? ""} empty={isEmptyOption(option)} onRemove={onRemove} />
  </div>
}

function OptionMenu({ option, vars, onChange }: { option: PlaybookOption, vars: string[], onChange: (option: PlaybookOption) => void }) {
  const [open, setOpen] = useState(false)
  const active = !!option.id || (option.conditions?.length ?? 0) > 0 || Object.keys(option.set ?? {}).length > 0 || Object.keys(option.inc ?? {}).length > 0 || (option.match?.length ?? 0) > 0 || !!option.say

  return <>
    <Button variant="ghost" size="icon-sm" aria-label="Option settings" className={cn(active && "text-primary font-bold")} onClick={() => setOpen(true)}>
      <SettingsIcon />
    </Button>
    <OptionSettingsDialog open={open} onOpenChange={setOpen} option={option} vars={vars} onChange={onChange} />
  </>
}
